import { HelpLadder, Message } from '../hooks/useLessonState';
import { GenerateContentResponse } from '../hooks/useLessonApi';
import { INTERFACE_TEXT } from '../constants/interfaceText';

// Re-export Step type for convenience
export interface Step {
  step_index: number;
  action_type: string;
  content: Record<string, unknown>;
}

export interface AudioUrls {
  natural: string;
  slow?: string;
}

// API interface for step handlers
export interface LessonApi {
  generateContent: (
    runId: string,
    stepVersion: number,
    signal?: AbortSignal
  ) => Promise<GenerateContentResponse>;
  fetchAudioUrl: (
    formId: string,
    speed: 'natural' | 'slow',
    signal?: AbortSignal
  ) => Promise<string>;
}

// Result type for step handlers
export interface StepHandlerResult {
  messages: Message[] | null; // null means duplicate, skip adding
  needsAutoContinue?: boolean;
  newStepVersion?: number;
  error?: string;
}

/** Which step and lesson part a message came from, set on every message. */
export const stepMeta = (step: Step): Pick<Message, 'stepKind' | 'partId'> => {
  const partId = step.content?.part_id;
  return {
    stepKind: step.action_type,
    partId: typeof partId === 'string' ? partId : null,
  };
};

/**
 * Whether the run is waiting for the learner to speak after this step (BR-008, BR-014):
 * a production prompt, or feedback asking for another try. Every other action
 * (modelled audio, context, explanations, persona lines) is the tutor's turn.
 * A give_feedback without `success: true` is a retry, as in handleGiveFeedbackStep.
 */
export const awaitsLearnerInput = (step: Step): boolean => {
  if (step.action_type === 'request_production') return true;
  if (step.action_type === 'give_feedback') {
    return step.content?.success !== true;
  }
  return false;
};

/**
 * Check if messages already contain a provide_context message for the given step.
 * Used to allow multiple provide_context messages (one per step) while avoiding
 * duplicates when the same step's provide_context is processed twice.
 */
export const hasProvideContextMessageForStep = (
  messages: Array<{ id: string }>,
  stepIndex: number
): boolean => {
  const prefix = `step-${stepIndex}-provide_context-`;
  return messages.some((msg) => msg.id.startsWith(prefix));
};

/**
 * Handles a provide_context step.
 * Returns messages to add, or null if duplicate.
 * If content is empty, generates it via API.
 */
export const handleProvideContextStep = async (
  step: Step,
  stepId: string,
  runId: string,
  stepVersion: number,
  existingMessages: Message[],
  api: LessonApi,
  signal?: AbortSignal
): Promise<StepHandlerResult> => {
  // Check if we already have a provide_context message for this step (step-index-aware)
  if (hasProvideContextMessageForStep(existingMessages, step.step_index)) {
    console.log(
      '[handleProvideContextStep] Already have provide_context message for this step, skipping duplicate'
    );
    return { messages: null };
  }

  // A start returns this step with no text, but with a part_id; the intro is generated via /content until it has text
  const hasText =
    typeof step.content?.text === 'string' && step.content.text !== '';

  if (!hasText) {
    // Content is empty - call /content endpoint to generate it
    console.log(
      '[handleProvideContextStep] provide_context has no content, calling /content endpoint'
    );

    try {
      const contentData = await api.generateContent(runId, stepVersion, signal);
      const contextText =
        (contentData.content?.text as string) || 'Welcome to your lesson!';

      console.log(
        '[handleProvideContextStep] Generated context text:',
        contextText
      );

      // Double-check for duplicates after async operation
      if (hasProvideContextMessageForStep(existingMessages, step.step_index)) {
        console.log(
          '[handleProvideContextStep] Duplicate detected after content generation, skipping'
        );
        return { messages: null };
      }

      const message: Message = {
        id: stepId,
        sender: 'tutor',
        content: contextText,
        timestamp: new Date().toISOString(),
        ...stepMeta(step),
      };

      return {
        messages: [message],
        needsAutoContinue: true,
        newStepVersion: contentData.step_version,
      };
    } catch (contentError) {
      console.error(
        '[handleProvideContextStep] Failed to generate content:',
        contentError
      );
      return {
        messages: null,
        error:
          contentError instanceof Error
            ? contentError.message
            : 'Failed to generate content',
      };
    }
  }

  // Content exists - display it normally
  const contextText = step.content.text as string;

  console.log(
    '[handleProvideContextStep] Adding provide_context message:',
    contextText,
    'stepId:',
    stepId
  );

  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: contextText,
    timestamp: new Date().toISOString(),
    ...stepMeta(step),
  };

  return {
    messages: [message],
    needsAutoContinue: true,
  };
};

/**
 * Fetches a phrase's natural and slow clip URLs. Rejects when the natural clip
 * can't be fetched; a failed slow clip only drops the Slower option, since the
 * natural one is enough to listen first (#249).
 */
export const fetchPhraseAudio = async (
  formId: string,
  api: LessonApi,
  signal?: AbortSignal
): Promise<AudioUrls> => {
  const natural = await api.fetchAudioUrl(formId, 'natural', signal);
  try {
    const slow = await api.fetchAudioUrl(formId, 'slow', signal);
    return { natural, slow };
  } catch (slowError) {
    console.error('[fetchPhraseAudio] Slow clip unavailable:', slowError);
    return { natural };
  }
};

/** The help ladder a request_production or retry give_feedback step carries; absent before any help is earned. */
const ladderOf = (step: Step): { ladder?: HelpLadder } => {
  const help = step.content?.help;
  if (!help || typeof help !== 'object') return {};
  const { level, meaning, audio_form_id, cue, phrase } = help as Record<
    string,
    unknown
  >;
  if (typeof level !== 'number' || level < 1) return {};
  const text = (value: unknown): string | null =>
    typeof value === 'string' && value ? value : null;
  return {
    ladder: {
      level,
      meaning: text(meaning),
      audioFormId: text(audio_form_id),
      cue: text(cue),
      phrase: text(phrase),
    },
  };
};

/** The short gloss of a modeled phrase (#342), for the side rail; absent when the step has none. */
const glossOf = (step: Step): { gloss?: string } => {
  const gloss = step.content?.gloss;
  return typeof gloss === 'string' && gloss ? { gloss } : {};
};

/** What a lesson persona line means, sent with its clip (BR-042); absent for other lines. */
const lineMeaningOf = (step: Step): { meaning?: string; note?: string } => {
  const { meaning, note } = step.content ?? {};
  return {
    ...(typeof meaning === 'string' && meaning ? { meaning } : {}),
    ...(typeof meaning === 'string' &&
    meaning &&
    typeof note === 'string' &&
    note
      ? { note }
      : {}),
  };
};

/**
 * Handles a play_audio step.
 * Fetches audio URLs and returns message with audio.
 * Falls back to message without audio on error.
 */
export const handlePlayAudioStep = async (
  step: Step,
  stepId: string,
  existingMessages: Message[],
  api: LessonApi,
  signal?: AbortSignal
): Promise<StepHandlerResult> => {
  // Extract form_id from content (backend returns it in content.form_id)
  const formId = (step.content?.form_id as string) || '';

  // Get prompt text and Greek text from content
  const promptText = (step.content?.prompt as string) || '';
  const targetText = (step.content?.text as string) || '';

  console.log(
    '[handlePlayAudioStep] play_audio - formId:',
    formId,
    'promptText:',
    promptText,
    'targetText:',
    targetText
  );

  // A persona line carries no commentary: the bubble is the Greek text and the player.
  const persona = step.content?.purpose === 'persona_utterance';
  const finalPromptText = promptText || '';

  if (!formId) {
    console.error(
      '[handlePlayAudioStep] play_audio action but no form_id in content. Content keys:',
      Object.keys(step.content || {})
    );
    // Show message without audio
    // Use prompt if available, otherwise targetText fallback, otherwise "Audio unavailable"
    const noFormIdContent =
      finalPromptText ||
      (persona
        ? ''
        : targetText
          ? `Listen to how to say '${targetText}'`
          : 'Audio unavailable');
    const message: Message = {
      id: stepId,
      sender: 'tutor',
      content: noFormIdContent,
      targetText: targetText,
      timestamp: new Date().toISOString(),
      ...stepMeta(step),
      audioUnavailable: 'missing',
    };
    return { messages: [message] };
  }

  // When we have formId, we can use targetText as fallback for prompt
  const finalPromptWithFallback =
    finalPromptText ||
    (!persona && targetText ? `Listen to how to say '${targetText}'` : '');

  try {
    const audioUrls = await fetchPhraseAudio(formId, api, signal);
    const message: Message = {
      id: stepId,
      sender: 'tutor',
      content:
        finalPromptWithFallback || (persona ? '' : 'Listen to the audio'),
      targetText: targetText,
      timestamp: new Date().toISOString(),
      ...stepMeta(step),
      ...glossOf(step),
      ...lineMeaningOf(step),
      audioUrls,
      audioFormId: formId,
      ...(persona ? { persona: true } : {}),
    };

    return { messages: [message] };
  } catch (audioError) {
    console.error('[handlePlayAudioStep] Failed to fetch audio:', audioError);
    console.error('[handlePlayAudioStep] Audio error details:', {
      formId,
      error:
        audioError instanceof Error ? audioError.message : String(audioError),
      stack: audioError instanceof Error ? audioError.stack : undefined,
    });
    // Show the phrase without a player; the learner can retry the fetch
    // (and skip if that fails too), since only a played clip advances (#249).
    const errorFallbackPrompt =
      finalPromptText ||
      (persona
        ? ''
        : targetText
          ? `Listen to how to say '${targetText}'`
          : 'Listen to the audio');
    const message: Message = {
      id: stepId,
      sender: 'tutor',
      content: errorFallbackPrompt,
      targetText: targetText,
      timestamp: new Date().toISOString(),
      ...stepMeta(step),
      ...glossOf(step),
      ...lineMeaningOf(step),
      audioFormId: formId,
      audioUnavailable: 'fetch_failed',
      ...(persona ? { persona: true } : {}),
    };
    return { messages: [message] };
  }
};

/**
 * Handles a request_production step.
 * Returns a message prompting the learner to practice speaking.
 * Does NOT auto-continue - waits for user to record audio.
 */
export const handleRequestProductionStep = (
  step: Step,
  stepId: string
): StepHandlerResult => {
  // Extract prompt from content (LLM-generated)
  const promptText =
    (step.content?.prompt as string) ||
    'Now try saying it yourself. Tap the mic to record.';

  console.log(
    '[handleRequestProductionStep] Adding request_production message:',
    promptText
  );

  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: promptText,
    timestamp: new Date().toISOString(),
    ...stepMeta(step),
    ...ladderOf(step),
    // The learner may pick any phrase they have learned: say so with a fixed label
    ...(step.content?.cue_kind === 'free'
      ? { label: INTERFACE_TEXT.yourChoiceLabel }
      : {}),
    // A cue by meaning (new or known phrase): also shown as a chip above the mic
    ...(step.content?.cue_kind === 'prompted' ||
    step.content?.cue_kind === 'known'
      ? { cue: true }
      : {}),
  };

  // No auto-continue - user must record audio first
  return { messages: [message] };
};

/**
 * Phrases the speech recogniser should expect for the learner's next attempt:
 * every phrase this turn accepts (`accepts`), else the single form. They are
 * sent as ASR hints only and never shown, so a cue by meaning does not give
 * the phrase away.
 */
export const phraseHintsFor = (
  content: Record<string, unknown> | undefined,
  fallbackForm?: { text?: string }
): string[] | null => {
  const accepts = content?.accepts;
  if (Array.isArray(accepts)) {
    const hints = accepts.filter(
      (item): item is string => typeof item === 'string' && item.length > 0
    );
    if (hints.length > 0) return hints;
  }
  return fallbackForm?.text ? [fallbackForm.text] : null;
};

/**
 * Handles a scene_beat step: a short line from the tutor that sets the scene
 * before the learner's turn. Auto-continues, like an explanation.
 */
export const handleSceneBeatStep = (
  step: Step,
  stepId: string
): StepHandlerResult => {
  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: (step.content?.text as string) || INTERFACE_TEXT.sceneBeatFallback,
    timestamp: new Date().toISOString(),
    ...stepMeta(step),
  };
  return { messages: [message], needsAutoContinue: true };
};

/**
 * Handles an explain_meaning step (persona step: explain what the persona said).
 * Returns a message with the explanation text.
 * Auto-continues immediately so the next /next call advances to the learner step.
 */
export const handleExplainMeaningStep = (
  step: Step,
  stepId: string
): StepHandlerResult => {
  const textContent =
    (step.content?.text as string) || INTERFACE_TEXT.explainMeaningFallback;

  console.log(
    '[handleExplainMeaningStep] Adding explain_meaning message:',
    textContent.substring(0, 50) + (textContent.length > 50 ? '...' : '')
  );

  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: textContent,
    timestamp: new Date().toISOString(),
    ...stepMeta(step),
  };
  const note = step.content?.note;
  if (typeof note === 'string' && note) message.note = note;
  const gloss = step.content?.gloss;
  if (typeof gloss === 'string' && gloss) message.gloss = gloss;

  return {
    messages: [message],
    needsAutoContinue: true,
  };
};

/**
 * Handles a give_feedback step.
 * Returns a message with the feedback text.
 * Auto-continues if successful, otherwise waits for retry.
 */
export const handleGiveFeedbackStep = async (
  step: Step,
  stepId: string
): Promise<StepHandlerResult> => {
  // Extract feedback text from content (LLM-generated). When success is true, use a positive
  // fallback so we never show "Good effort! Try again." for a correct pronunciation.
  const rawText = (step.content?.text as string) || '';
  const feedbackText =
    rawText.trim() ||
    ((step.content?.success as boolean)
      ? 'Well done!'
      : 'Good effort! Try again.');

  // Extract success and retry flags
  const success = (step.content?.success as boolean) ?? false;
  const retry = (step.content?.retry as boolean) ?? false;

  const formAttempted = step.content?.form_attempted as
    | {
        id?: string;
        text?: string;
      }
    | undefined;

  console.log(
    '[handleGiveFeedbackStep] give_feedback - success:',
    success,
    'retry:',
    retry,
    'formId:',
    formAttempted?.id
  );

  // A retry never shows the phrase before the ladder reaches it (BR-051); a success may.
  const ladder = ladderOf(step);
  const targetText = success
    ? formAttempted?.text
    : (ladder.ladder?.phrase ?? undefined);

  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: feedbackText,
    targetText,
    timestamp: new Date().toISOString(),
    ...ladder,
    success,
    ...stepMeta(step),
  };

  // Auto-continue if successful, otherwise wait for retry
  return {
    messages: [message],
    needsAutoContinue: success,
  };
};

/**
 * Handles a provide_introduction step (assessment start).
 * Returns a tutor text bubble with the intro message.
 * Auto-continues to the first conversation step.
 */
export function handleProvideIntroductionStep(
  step: Step,
  stepId: string
): StepHandlerResult {
  return {
    messages: [
      {
        id: stepId,
        sender: 'tutor' as const,
        content: (step.content?.message as string) ?? '',
        timestamp: new Date().toISOString(),
        ...stepMeta(step),
      },
    ],
    needsAutoContinue: true,
  };
}

/**
 * Handles a fallback step (unknown action type).
 * Returns a message with available text content.
 */
export const handleFallbackStep = (
  step: Step,
  stepId: string
): StepHandlerResult => {
  const textContent =
    (step.content?.text as string) ||
    (step.content?.prompt as string) ||
    'Welcome to your lesson!';

  // Warn about unexpected action types
  console.warn(
    '[handleFallbackStep] Unexpected action_type:',
    step.action_type,
    'Using fallback handler'
  );

  console.log(
    '[handleFallbackStep] Adding fallback message with textContent:',
    textContent
  );

  const message: Message = {
    id: stepId,
    sender: 'tutor',
    content: textContent,
    timestamp: new Date().toISOString(),
    ...stepMeta(step),
  };

  return { messages: [message] };
};
